import React, { useEffect, useState } from "react";
import {
  X,
  Users,
  MessageSquare,
  BarChart,
  ShoppingBag,
  CreditCard,
  TrendingUp,
  Heart,
  Check,
  Sparkles,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import { cn } from "@/lib/utils";
import { OnboardingProgressService } from "@/services/OnboardingProgressService";
import { useNavigate } from "react-router";
import FeatureUtils from "@/utility/FeatureUtils";
import { updateOrganization } from "@/services/OrganizationService";
import { usePermissions } from "@/hooks/usePermissions";
import { useOnboardingCompletionTimer } from "@/hooks/useOnboardingCompletionTimer";

interface OnboardingSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TaskCard {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: "purple" | "blue" | "pink" | "green" | "multi-color";
  isCompleted: boolean;
  isClickable: boolean;
  onClick?: () => void;
}

interface Step {
  id: string;
  title: string;
  subtitle: string;
  isCompleted: boolean;
  isAvailable: boolean;
  tasks: TaskCard[];
}

const calculateOverallProgressFromOnboarding = (
  onboardingProgress: any,
  appType: "CRM" | "ANALYTICS" = "CRM"
): { completed: number; total: number; percentage: number } => {
  if (!onboardingProgress) {
    return {
      completed: 0,
      total: appType === "ANALYTICS" ? 6 : 7,
      percentage: 0
    };
  }

  let allSteps: string[] = [];
  let total = 7;

  if (appType === "CRM") {
    allSteps = [
      onboardingProgress.customer,
      onboardingProgress.customersReportOverview,
      onboardingProgress.smsCampaigns,
      onboardingProgress.productCatalog,
      onboardingProgress.transactions,
      onboardingProgress.salesReportOverview,
      onboardingProgress.automatedCampaigns
    ];
    total = 7;
  } else if (appType === "ANALYTICS") {
    allSteps = [
      onboardingProgress.customer,
      onboardingProgress.behaviorAndEngagementReport,
      onboardingProgress.purchasePatternReport,
      onboardingProgress.salesReportOverview,
      onboardingProgress.trendsAndPatternsReport,
      onboardingProgress.productInsightsReport
    ];
    total = 6;
  }

  const completed = allSteps.filter((step) => step === "DONE").length;
  const percentage = Math.round((completed / total) * 100);

  return { completed, total, percentage };
};

const getAppSpecificSteps = (
  appType: "CRM" | "ANALYTICS" | undefined,
  onboardingProgress: any,
  navigate: (path: string) => void,
  syncDataResponse: any
): Step[] => {
  const isCustomerSyncInProgress = syncDataResponse?.customer < 100;
  const isOrderSyncInProgress = syncDataResponse?.orders < 100;
  if (appType === "CRM") {
    return [
      {
        id: "step1",
        title: isCustomerSyncInProgress
          ? "Step 1: Customer Sync In Progress..."
          : "Step 1: Customer Sync Step",
        subtitle: isCustomerSyncInProgress
          ? "Syncing customer data from your POS system..."
          : `Progress: ${getCompletedCount([
              onboardingProgress?.customer,
              onboardingProgress?.customersReportOverview,
              onboardingProgress?.smsCampaigns
            ])}/3 tasks`,
        isCompleted:
          getCompletedCount([
            onboardingProgress?.customer,
            onboardingProgress?.customersReportOverview,
            onboardingProgress?.smsCampaigns
          ]) === 3,
        isAvailable: true,
        tasks: [
          {
            id: "customer",
            label: "View Your Customer List",
            icon: Users,
            color: "purple",
            isCompleted: onboardingProgress?.customer === "DONE",
            isClickable: true,
            onClick: () => navigate("/customers")
          },
          {
            id: "smsCampaigns",
            label: "Send Your First SMS Campaign",
            icon: MessageSquare,
            color: "purple",
            isCompleted: onboardingProgress?.smsCampaigns === "DONE",
            isClickable: true,
            onClick: () => navigate("/campaigns/broadcast")
          },
          {
            id: "customersReportOverview",
            label: "View Your Customer Reports",
            icon: BarChart,
            color: "multi-color",
            isCompleted: onboardingProgress?.customersReportOverview === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/customers")
          }
        ]
      },
      {
        id: "step2",
        title: isOrderSyncInProgress
          ? "Step 2: Product Sync In Progress..."
          : "Step 2: Product Sync Completed",
        subtitle: isOrderSyncInProgress
          ? "Syncing product and order data from your POS system..."
          : `Progress: ${getCompletedCount([
              onboardingProgress?.productCatalog,
              onboardingProgress?.transactions,
              onboardingProgress?.salesReportOverview,
              onboardingProgress?.automatedCampaigns
            ])}/4 tasks`,
        isCompleted:
          getCompletedCount([
            onboardingProgress?.productCatalog,
            onboardingProgress?.transactions,
            onboardingProgress?.salesReportOverview,
            onboardingProgress?.automatedCampaigns
          ]) === 4,
        isAvailable: true,
        tasks: [
          {
            id: "productCatalog",
            label: "View Your Products",
            icon: ShoppingBag,
            color: "blue",
            isCompleted: onboardingProgress?.productCatalog === "DONE",
            isClickable: true,
            onClick: () => navigate("/products")
          },
          {
            id: "transactions",
            label: "View Your Transactions",
            icon: CreditCard,
            color: "blue",
            isCompleted: onboardingProgress?.transactions === "DONE",
            isClickable: true,
            onClick: () => navigate("/transactions")
          },
          {
            id: "salesReportOverview",
            label: "View Your Sales Reports",
            icon: TrendingUp,
            color: "purple",
            isCompleted: onboardingProgress?.salesReportOverview === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/sales")
          },
          {
            id: "automatedCampaigns",
            label: "Send Automated Messages",
            icon: Heart,
            color: "pink",
            isCompleted: onboardingProgress?.automatedCampaigns === "DONE",
            isClickable: true,
            onClick: () => navigate("/automated-campaigns")
          }
        ]
      }
    ];
  } else if (appType === "ANALYTICS") {
    return [
      {
        id: "step1",
        title: isCustomerSyncInProgress
          ? "Step 1: Customer Sync In Progress..."
          : "Step 1: Customer Sync Step",
        subtitle: isCustomerSyncInProgress
          ? "Syncing customer data from your POS system..."
          : `Progress: ${getCompletedCount([
              onboardingProgress?.customer,
              onboardingProgress?.behaviorAndEngagementReport,
              onboardingProgress?.purchasePatternReport
            ])}/3 tasks`,
        isCompleted:
          getCompletedCount([
            onboardingProgress?.customer,
            onboardingProgress?.behaviorAndEngagementReport,
            onboardingProgress?.purchasePatternReport
          ]) === 3,
        isAvailable: true,
        tasks: [
          {
            id: "customer",
            label: "Customer Overview",
            icon: Users,
            color: "purple",
            isCompleted: onboardingProgress?.customer === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/customers/overview")
          },
          {
            id: "behaviorAndEngagementReport",
            label: "Behavior & Engagement",
            icon: MessageSquare,
            color: "purple",
            isCompleted:
              onboardingProgress?.behaviorAndEngagementReport === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/customers/behavior")
          },
          {
            id: "purchasePatternReport",
            label: "Purchase Pattern Analysis",
            icon: BarChart,
            color: "multi-color",
            isCompleted: onboardingProgress?.purchasePatternReport === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/customers/purchase-pattern")
          }
        ]
      },
      {
        id: "step2",
        title: isOrderSyncInProgress
          ? "Step 2: Order Sync In Progress..."
          : "Step 2: Order Sync Completed",
        subtitle: isOrderSyncInProgress
          ? "Syncing order and transaction data from your POS system..."
          : `Progress: ${getCompletedCount([
              onboardingProgress?.salesReportOverview,
              onboardingProgress?.trendsAndPatternsReport,
              onboardingProgress?.productInsightsReport,
              onboardingProgress?.transactionReportOverview
            ])}/4 tasks`,
        isCompleted:
          getCompletedCount([
            onboardingProgress?.salesReportOverview,
            onboardingProgress?.trendsAndPatternsReport,
            onboardingProgress?.productInsightsReport,
            onboardingProgress?.transactionReportOverview
          ]) === 4,
        isAvailable: true,
        tasks: [
          {
            id: "salesReportOverview",
            label: "Sales Overview",
            icon: TrendingUp,
            color: "purple",
            isCompleted: onboardingProgress?.salesReportOverview === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/sales/overview")
          },
          {
            id: "trendsAndPatternsReport",
            label: "Trends and Patterns",
            icon: BarChart,
            color: "blue",
            isCompleted: onboardingProgress?.trendsAndPatternsReport === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/sales/trends")
          },
          {
            id: "productInsightsReport",
            label: "Product Insights",
            icon: ShoppingBag,
            color: "blue",
            isCompleted: onboardingProgress?.productInsightsReport === "DONE",
            isClickable: true,
            onClick: () => navigate("/reports/sales/products")
          }
        ]
      }
    ];
  }

  // Fallback to CRM steps if appType is not recognized
  return [
    {
      id: "step1",
      title: isCustomerSyncInProgress
        ? "Step 1: Customer Sync In Progress..."
        : "Step 1: Get Your Customers Ready.",
      subtitle: isCustomerSyncInProgress
        ? "Syncing customer data from your POS system..."
        : `Progress: ${getCompletedCount([
            onboardingProgress?.customer,
            onboardingProgress?.customersReportOverview,
            onboardingProgress?.smsCampaigns
          ])}/3 tasks`,
      isCompleted:
        getCompletedCount([
          onboardingProgress?.customer,
          onboardingProgress?.customersReportOverview,
          onboardingProgress?.smsCampaigns
        ]) === 3,
      isAvailable: true,
      tasks: [
        {
          id: "customer",
          label: "View Your Customer List",
          icon: Users,
          color: "purple",
          isCompleted: onboardingProgress?.customer === "DONE",
          isClickable: true,
          onClick: () => navigate("/customers")
        },
        {
          id: "smsCampaigns",
          label: "Send Your First SMS Campaign",
          icon: MessageSquare,
          color: "purple",
          isCompleted: onboardingProgress?.smsCampaigns === "DONE",
          isClickable: true,
          onClick: () => navigate("/campaigns/broadcast")
        },
        {
          id: "customersReportOverview",
          label: "View Your Customer Reports",
          icon: BarChart,
          color: "multi-color",
          isCompleted: onboardingProgress?.customersReportOverview === "DONE",
          isClickable: true,
          onClick: () => navigate("/reports/customers")
        }
      ]
    },
    {
      id: "step2",
      title: isOrderSyncInProgress
        ? "Step 2: Product Sync In Progress..."
        : "Step 2: Get Your Products Ready.",
      subtitle: isOrderSyncInProgress
        ? "Syncing product and order data from your POS system..."
        : `Progress: ${getCompletedCount([
            onboardingProgress?.productCatalog,
            onboardingProgress?.transactions,
            onboardingProgress?.salesReportOverview,
            onboardingProgress?.automatedCampaigns
          ])}/4 tasks`,
      isCompleted:
        getCompletedCount([
          onboardingProgress?.productCatalog,
          onboardingProgress?.transactions,
          onboardingProgress?.salesReportOverview,
          onboardingProgress?.automatedCampaigns
        ]) === 4,
      isAvailable: true,
      tasks: [
        {
          id: "productCatalog",
          label: "View Your Products",
          icon: ShoppingBag,
          color: "blue",
          isCompleted: onboardingProgress?.productCatalog === "DONE",
          isClickable: true,
          onClick: () => navigate("/products")
        },
        {
          id: "transactions",
          label: "View Your Transactions",
          icon: CreditCard,
          color: "blue",
          isCompleted: onboardingProgress?.transactions === "DONE",
          isClickable: true,
          onClick: () => navigate("/transactions")
        },
        {
          id: "salesReportOverview",
          label: "View Your Sales Reports",
          icon: TrendingUp,
          color: "purple",
          isCompleted: onboardingProgress?.salesReportOverview === "DONE",
          isClickable: true,
          onClick: () => navigate("/reports/sales")
        },
        {
          id: "automatedCampaigns",
          label: "Send Automated Messages",
          icon: Heart,
          color: "pink",
          isCompleted: onboardingProgress?.automatedCampaigns === "DONE",
          isClickable: true,
          onClick: () => navigate("/automated-campaigns")
        }
      ]
    }
  ];
};

const getCompletedCount = (steps: string[]): number => {
  return steps.filter((step) => step === "DONE").length;
};

const TaskCardComponent: React.FC<{ task: TaskCard }> = ({ task }) => {
  const Icon = task.icon;

  const colorClasses = {
    purple: "border-purple-200 bg-purple-50 text-purple-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    pink: "border-pink-200 bg-pink-50 text-pink-700",
    green: "border-green-200 bg-green-50 text-green-700",
    "multi-color":
      "border-gray-200 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 text-gray-700"
  };

  const isCloverBuild = import.meta.env.__IS_CLOVER_BUILD__;

  return (
    <div
      className={cn(
        "flex flex-col items-center p-2 rounded-lg border transition-all duration-200 cursor-pointer",
        "hover:scale-105 hover:shadow-md active:scale-95",
        colorClasses[task.color],
        !task.isClickable && "opacity-50 cursor-not-allowed",
        task.isCompleted && "ring-2 ring-green-500 bg-green-50",
        isCloverBuild && "min-h-[48px] min-w-[48px] p-2"
      )}
      onClick={task.isClickable ? task.onClick : undefined}
      role={task.isClickable ? "button" : "presentation"}
      aria-label={`${task.label}${task.isCompleted ? " - Completed" : " - Incomplete"}`}
    >
      <div className="relative">
        <Icon className={cn("h-8 w-8", isCloverBuild && "h-10 w-10")} />
        {task.isCompleted && (
          <Check className="absolute -top-1 -right-1 h-4 w-4 text-green-600 bg-white rounded-full" />
        )}
      </div>
      <span
        className={cn(
          "text-xs font-medium mt-1 text-center",
          isCloverBuild && "text-sm leading-5"
        )}
      >
        {task.isCompleted ? `${task.label}` : task.label}
      </span>
    </div>
  );
};

const CelebrationScreen: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <div className="relative">
        <Sparkles className="h-16 w-16 text-yellow-500 mb-4" />
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="text-3xl animate-bounce">🎉</div>
          </div>
        )}
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        All done 🎉 MarketBuzz is ready to roll!
      </h2>
      <p className="text-gray-600 mb-6">
        You've completed all the setup steps. Your MarketBuzz dashboard is now
        fully configured and ready to help you grow your business.
      </p>
      <Button
        onClick={onClose}
        className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-md"
      >
        Get Started
      </Button>
    </div>
  );
};

export const OnboardingSidePanel: React.FC<OnboardingSidePanelProps> = ({
  isOpen,
  onClose
}) => {
  const {
    onboardingProgress,
    setOnboardingProgress,
    setShowOnboardingFlow,
    syncDataResponse
  } = useOrganizationStore();

  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);

  // Check appType using FeatureUtils methods like Header.tsx
  const isCRM = FeatureUtils.isCRMEnabled(permissions);
  const isAnalytics = FeatureUtils.isAnalyticsEnabled(permissions);

  let appType: "CRM" | "ANALYTICS" = "CRM";
  if (isAnalytics) {
    appType = "ANALYTICS";
  } else if (isCRM) {
    appType = "CRM";
  }

  // Only initialize with defaults if onboardingProgress is completely null/undefined
  // This preserves any existing completed tasks from the database
  const currentOnboardingProgress = onboardingProgress || {
    customer: "IN_PROGRESS",
    customersReportOverview: "IN_PROGRESS",
    smsCampaigns: "IN_PROGRESS",
    productCatalog: "IN_PROGRESS",
    transactions: "IN_PROGRESS",
    salesReportOverview: "IN_PROGRESS",
    automatedCampaigns: "IN_PROGRESS",
    behaviorAndEngagementReport: "IN_PROGRESS",
    purchasePatternReport: "IN_PROGRESS",
    transactionReportOverview: "IN_PROGRESS",
    trendsAndPatternsReport: "IN_PROGRESS",
    productInsightsReport: "IN_PROGRESS"
  };

  const progress = calculateOverallProgressFromOnboarding(
    currentOnboardingProgress,
    appType
  );
  const steps = getAppSpecificSteps(
    appType,
    currentOnboardingProgress,
    navigate,
    syncDataResponse
  );

  const isCloverBuild = import.meta.env.__IS_CLOVER_BUILD__;

  // Check sync status
  const isCustomerSyncInProgress = syncDataResponse.customer < 100;
  const isOrderSyncInProgress = syncDataResponse.orders < 100;

  // Check if all steps are completed
  const isAllComplete = progress.completed === progress.total;

  // Use persistent completion timer
  const {
    timeRemaining: completionTimeRemaining,
    isTimerActive,
    startTimer,
    resumeTimer,
    clearTimer
  } = useOnboardingCompletionTimer();
  const timerStartedRef = React.useRef(false);
  const apiCallInProgressRef = React.useRef(false);

  // Handle completion - start or resume timer when all steps are completed AND panel is open
  React.useEffect(() => {
    if (isAllComplete && isOpen) {
      if (isTimerActive) {
        // Timer is already active, resume it silently
        resumeTimer(async () => {
          if (apiCallInProgressRef.current) return;
          apiCallInProgressRef.current = true;
          try {
            await updateOrganization({ showOnboardingFlow: false });
            setShowOnboardingFlow(false);
          } catch (error) {
            console.error(
              "❌ Failed to update organization on completion:",
              error
            );
          } finally {
            apiCallInProgressRef.current = false;
          }
        });
      } else if (!timerStartedRef.current) {
        // Start new timer only once
        timerStartedRef.current = true;
        startTimer(async () => {
          if (apiCallInProgressRef.current) return;
          apiCallInProgressRef.current = true;
          try {
            await updateOrganization({ showOnboardingFlow: false });
            setShowOnboardingFlow(false);
          } catch (error) {
            console.error(
              "❌ Failed to update organization on completion:",
              error
            );
          } finally {
            apiCallInProgressRef.current = false;
          }
        });
      }
    } else if (!isOpen) {
      // Clear timer when panel is closed
      clearTimer();
      timerStartedRef.current = false;
      apiCallInProgressRef.current = false;
    }
  }, [
    isAllComplete,
    isOpen,
    isTimerActive,
    startTimer,
    resumeTimer,
    clearTimer,
    setShowOnboardingFlow
  ]);

  // Reset timer ref when completion status changes
  React.useEffect(() => {
    if (!isAllComplete) {
      timerStartedRef.current = false;
      apiCallInProgressRef.current = false;
    }
  }, [isAllComplete]);

  const handleClose = async () => {
    setIsClosing(true);

    // Save progress to backend if there are completed steps
    if (progress.completed > 0) {
      try {
        await OnboardingProgressService.getInstance().saveProgressOnSessionExpiry();
      } catch (error) {
        console.error("Failed to save progress:", error);
      }
    }

    // Clear the panel state from localStorage when user manually closes
    localStorage.removeItem("onboarding-panel-is-open");
    localStorage.setItem("onboarding-panel-closed", "true");

    // Close modal after animation
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const handleSkip = async () => {
    await handleClose();
  };

  const handleTaskClick = async (taskId: string) => {
    try {
      // Preserve all existing completed tasks and only update the specific task to DONE
      const updatedProgress = {
        ...currentOnboardingProgress,
        [taskId]: "DONE"
      } as any;

      // Update local state
      setOnboardingProgress(updatedProgress);

      // Update backend with the complete progress object
      await updateOrganization({
        onboardingProgress: updatedProgress
      });

      // Check if all steps are completed
      const newProgress = calculateOverallProgressFromOnboarding(
        updatedProgress,
        appType
      );
      if (newProgress.completed === newProgress.total) {
        // Mark onboarding as complete - let the timer handle the flow hiding
        await OnboardingProgressService.getInstance().checkAndUpdateOnboardingCompletion();
      }
    } catch (error) {
      console.error("Failed to update onboarding progress:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100vw",
        height: "100vh"
      }}
    >
      {/* Backdrop */}
      <div
        className="fixed bg-black bg-opacity-30 transition-opacity duration-300"
        onClick={handleClose}
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 1
        }}
      />

      {/* Side Panel */}
      <div
        className={cn(
          "fixed right-0 top-0 h-screen bg-white shadow-xl transform transition-transform duration-300 ease-in-out",
          // Desktop: 40-50% width, Mobile: full width
          "w-full sm:w-2/5 md:w-1/2 lg:w-2/5 xl:w-1/3",
          isClosing ? "translate-x-full" : "translate-x-0",
          isCloverBuild && "w-full min-h-[48px] min-w-[48px]"
        )}
        style={{ zIndex: 2 }}
      >
        <div className="flex flex-col h-screen">
          {/* Completion Banner */}
          {isAllComplete && (
            <div className="bg-teal-50 border-l-4 border-teal-400 p-4 m-4 rounded-r-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-teal-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-teal-800">
                    All done 🎉 MarketBuzz is ready to roll!
                    {completionTimeRemaining && completionTimeRemaining > 0 && (
                      <span className="ml-2 text-xs text-teal-600">
                        (Auto-hide in {Math.floor(completionTimeRemaining / 60)}
                        :
                        {(completionTimeRemaining % 60)
                          .toString()
                          .padStart(2, "0")}
                        )
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between p-2">
            <div className="flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-yellow-500" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Get Started with MarketBuzz
                </h2>
                <p className="text-sm text-gray-600">
                  {progress.completed}/{progress.total} Steps Completed
                </p>
              </div>
            </div>
            <Button
              onClick={handleClose}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="px-6">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-teal-500 h-2 rounded-full transition-all duration-500 ease-in-out"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isAllComplete ? (
              <CelebrationScreen onClose={handleClose} />
            ) : steps.length > 0 ? (
              <div className="p-6 space-y-8">
                {steps.map((step) => (
                  <div key={step.id} className="space-y-4">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold",
                          step.isCompleted
                            ? "bg-green-500 text-white"
                            : step.isAvailable
                              ? "bg-teal-500 text-white"
                              : "bg-gray-300 text-gray-600"
                        )}
                      >
                        {step.isCompleted ? (
                          <Check className="h-4 w-4" />
                        ) : step.id === "step1" && isCustomerSyncInProgress ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : step.id === "step2" && isOrderSyncInProgress ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : step.id === "step1" ? (
                          "1"
                        ) : (
                          "2"
                        )}
                      </div>
                      <div>
                        <h3
                          className={cn(
                            "font-semibold",
                            !step.isAvailable && "text-gray-400"
                          )}
                        >
                          {step.title}
                        </h3>
                        <p className="text-sm text-gray-600">{step.subtitle}</p>
                      </div>
                    </div>

                    {step.id === "step2" ? (
                      // Step 2: 3+1 layout (3 cards in first row, 1 card in second row)
                      <div className="space-y-4">
                        {/* First row: 3 cards */}
                        <div
                          className={cn(
                            "grid gap-4 grid-cols-3",
                            isCloverBuild && "grid-cols-2 gap-4"
                          )}
                        >
                          {step.tasks.slice(0, 3).map((task) => (
                            <TaskCardComponent
                              key={task.id}
                              task={{
                                ...task,
                                onClick: task.isClickable
                                  ? () => {
                                      // navigate to the task page first
                                      task.onClick?.();
                                      // wait for 300ms before clicking the task
                                      setTimeout(() => {
                                        handleTaskClick(task.id);
                                      }, 300);
                                    }
                                  : undefined
                              }}
                            />
                          ))}
                        </div>
                        {/* Second row: 1 card */}
                        {step.tasks.length > 3 && (
                          <div className="grid gap-4 grid-cols-3">
                            <TaskCardComponent
                              key={step.tasks[3].id}
                              task={{
                                ...step.tasks[3],
                                onClick: step.tasks[3].isClickable
                                  ? () => {
                                      handleTaskClick(step.tasks[3].id);
                                      step.tasks[3].onClick?.();
                                    }
                                  : undefined
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      // Step 1: 3 columns layout
                      <div
                        className={cn(
                          "grid gap-4 grid-cols-3",
                          isCloverBuild && "grid-cols-2 gap-4"
                        )}
                      >
                        {step.tasks.map((task) => (
                          <TaskCardComponent
                            key={task.id}
                            task={{
                              ...task,
                              onClick: task.isClickable
                                ? () => {
                                    // navigate to the task page first
                                    task.onClick?.();
                                    // wait for 300ms before clicking the task
                                    setTimeout(() => {
                                      handleTaskClick(task.id);
                                    }, 300);
                                  }
                                : undefined
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center">
                <p className="text-gray-500">Loading onboarding steps...</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {!isAllComplete && (
            <div>
              <Button
                onClick={handleSkip}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Skip for now
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
